import { Backdrop, CircularProgress, Container, Grid } from '@mui/material';
import React, { useEffect } from 'react';
import InvoicingHeader from '../components/InvoicingAndPayroll/InvoicingHeader';
import CompletedReviews from '../components/InvoicingAndPayroll/CompletedReviews';
import AncillaryHours from '../components/InvoicingAndPayroll/AncillaryHours';
import { useGet } from '../lib/api/hooks';
import {
    InvoicingAndPayrollHeader,
    InvoicingHeaderModel
} from '../lib/models/InvoicingAndPayrollModel';
import Moment from 'moment';
import useTrackPageView from '../lib/hooks/use-track-page-view';
import useTrackEvent from '../lib/hooks/use-track-event';

export type ReviewProps = {
    ancillaryMinutes: number;
    completedReviewMinutes: number;
};

const InvoicingAndPayrollPage = () => {
    const trackPageView = useTrackPageView();
    const trackEvent = useTrackEvent();
    const { data, loading, refreshData, status } =
        useGet<InvoicingAndPayrollHeader>('invoice-payroll/current');
    const calculateTotalCycleHours = ({
        ancillaryMinutes,
        completedReviewMinutes
    }: ReviewProps) => {
        return ((completedReviewMinutes + ancillaryMinutes) / 60).toFixed(2).toString();
    };

    useEffect(() => {
        trackPageView('Invoicing And Payroll Page');
    }, [trackPageView]);

    useEffect(() => {
        if (!loading) {
            if (status >= 200 && status < 400 && data) {
                trackEvent('Invoicing And Payroll Minutes Data', {
                    status: 'success',
                    data
                });
            } else {
                trackEvent('Invoicing And Payroll Minutes Data', {
                    status: 'failure',
                    data
                });
            }
        }
    }, [loading, status, data, trackEvent]);

    const calculateTotalHours = (hours: number) => {
        return (hours / 60).toFixed(2).toString();
    };

    const headerData: InvoicingHeaderModel = {
        cycleInterval:
            data && data.startDate && data.endDate
                ? Moment(data.startDate).format('MM/DD/YYYY') +
                  ' - ' +
                  Moment(data.endDate).format('MM/DD/YYYY')
                : '',
        cycleTotal: calculateTotalCycleHours({
            ancillaryMinutes: data && data.ancillaryMinutesNumber ? data.ancillaryMinutesNumber : 0,
            completedReviewMinutes: data && data.reviewMinutesNumber ? data.reviewMinutesNumber : 0
        })
    };

    return (
        <>
            {!loading && data ? (
                <>
                    <InvoicingHeader data={headerData} />
                    <Container maxWidth={'xl'} sx={{ paddingTop: 2 }}>
                        <Grid container spacing={1}>
                            <Grid item lg={7} md={12} sm={12} xs={12}>
                                <CompletedReviews
                                    currentCycleId={data.id}
                                    totalCompletedReviewHours={calculateTotalHours(
                                        data && data.reviewMinutesNumber
                                            ? data.reviewMinutesNumber
                                            : 0
                                    )}
                                />
                            </Grid>
                            <Grid item lg={5} md={12} sm={12} xs={12}>
                                <AncillaryHours
                                    currentCycleId={data.id}
                                    cycleInterval={headerData.cycleInterval}
                                    totalAncillaryHours={calculateTotalHours(
                                        data && data.ancillaryMinutesNumber
                                            ? data.ancillaryMinutesNumber
                                            : 0
                                    )}
                                    refreshData={refreshData}
                                />
                            </Grid>
                        </Grid>
                    </Container>
                </>
            ) : (
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </>
    );
};

export default InvoicingAndPayrollPage;
